import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, X, MessageSquare } from 'lucide-react';

const TIPOS = [
  { value: 'unica_escolha', label: 'Única escolha', descricao: 'Rádio — 1 resposta só' },
  { value: 'multipla_escolha', label: 'Múltipla escolha', descricao: 'Checkbox — várias respostas' },
  { value: 'aberta', label: 'Aberta', descricao: 'Texto livre, sem opções' }
];

const inputClasses = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all';

// Formulário de criação de pergunta nova. Perguntas de escolha (única ou
// múltipla) exigem pelo menos 2 opções — validado aqui e reforçado pelo
// backend.
const PerguntaForm = ({ onCriar, onCancelar }) => {
  const [enviando, setEnviando] = useState(false);
  const { register, control, handleSubmit, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      texto: '',
      tipo: 'unica_escolha',
      obrigatoria: true,
      opcoes: [{ texto: '' }, { texto: '' }]
    }
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'opcoes' });
  const tipoSelecionado = watch('tipo');
  const precisaOpcoes = tipoSelecionado !== 'aberta';

  const onSubmit = async (dados) => {
    if (precisaOpcoes) {
      const opcoesValidas = dados.opcoes.map((o) => o.texto.trim()).filter(Boolean);
      if (opcoesValidas.length < 2) {
        return; // erro já mostrado via required no input
      }
    }

    setEnviando(true);
    try {
      await onCriar({
        texto: dados.texto.trim(),
        tipo: dados.tipo,
        obrigatoria: dados.obrigatoria,
        opcoes: precisaOpcoes ? dados.opcoes.map((o) => ({ texto: o.texto.trim() })).filter((o) => o.texto) : undefined
      });
      reset();
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="rounded-xl border-2 border-primary-100 bg-primary-50/30 p-4 sm:p-5 space-y-4">
      <div className="flex items-center gap-2 text-primary-700">
        <MessageSquare className="w-4 h-4" />
        <h3 className="text-sm font-semibold">Nova pergunta</h3>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Texto da pergunta</label>
        <input
          {...register('texto', { required: 'Texto é obrigatório' })}
          className={inputClasses}
          placeholder="Ex: Você é a favor da nova praça?"
        />
        {errors.texto && <p className="text-sm text-red-600 mt-1">{errors.texto.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {TIPOS.map((t) => (
            <label
              key={t.value}
              className={`flex flex-col gap-0.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                tipoSelecionado === t.value
                  ? 'border-[#40A1E6] bg-[#40A1E6]/5'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <input type="radio" value={t.value} {...register('tipo')} className="sr-only" />
              <span className="text-sm font-medium text-gray-800">{t.label}</span>
              <span className="text-xs text-gray-500">{t.descricao}</span>
            </label>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" {...register('obrigatoria')} className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500" />
        Resposta obrigatória
      </label>

      {precisaOpcoes && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Opções (mínimo 2)
          </label>
          <div className="space-y-2">
            {fields.map((field, index) => (
              <div key={field.id}>
                <div className="flex items-center gap-2">
                  <input
                    {...register(`opcoes.${index}.texto`, { required: index < 2 ? 'Obrigatório' : false })}
                    className={inputClasses}
                    placeholder={`Opção ${index + 1}`}
                  />
                  {fields.length > 2 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {errors.opcoes?.[index]?.texto && (
                  <p className="text-sm text-red-600 mt-1">{errors.opcoes[index].texto.message}</p>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => append({ texto: '' })}
            className="mt-2 text-sm text-primary-600 hover:bg-primary-100/60 rounded-lg px-2 py-1 -ml-2 flex items-center gap-1 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Adicionar opção
          </button>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button type="submit" disabled={enviando} className="btn-primary">
          {enviando ? 'Criando...' : 'Criar pergunta'}
        </button>
        <button
          type="button"
          onClick={onCancelar}
          className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
};

export default PerguntaForm;
